import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { ApiError } from "../lib/api";
import { Icon, type IconName } from "./icons";

/**
 * The head of a page: a way back and a small label on detail pages, then
 * the title with the page's actions beside it, and a line of description.
 */
export function PageHead({
  eyebrow,
  title,
  lede,
  back,
  children,
}: {
  eyebrow?: string;
  title: ReactNode;
  lede?: ReactNode;
  back?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="page-head">
      {(back || eyebrow) && (
        <div className="page-over">
          {back}
          {eyebrow && <span className="page-eyebrow">{eyebrow}</span>}
        </div>
      )}
      <div className="page-title">
        <h1>{title}</h1>
        {children && <div className="page-actions">{children}</div>}
      </div>
      {lede && <div className="page-lede">{lede}</div>}
    </header>
  );
}

/** A panel of a page: a titled card, with anything that qualifies it on the right. */
export function Section({ title, aside, className, children }: { title: string; aside?: ReactNode; className?: string; children: ReactNode }) {
  const id = useId();
  return (
    <section className={["panel", className].filter(Boolean).join(" ")} aria-labelledby={id}>
      <header className="panel-head">
        <h2 id={id} className="panel-title">
          {title}
        </h2>
        {aside && <div className="panel-aside">{aside}</div>}
      </header>
      <div className="panel-body">{children}</div>
    </section>
  );
}

type Tone = "ok" | "warn" | "danger" | "muted" | "accent";

export function Badge({ tone = "muted", children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className="badge" data-tone={tone}>
      {children}
    </span>
  );
}

export function Notice({ tone = "muted", title, children }: { tone?: Tone; title?: string; children?: ReactNode }) {
  return (
    <div className="notice" data-tone={tone} role={tone === "danger" ? "alert" : undefined}>
      {title && <strong>{title}</strong>}
      {children && <div>{children}</div>}
    </div>
  );
}

export function ErrorNote({ error }: { error?: ApiError | null }) {
  if (!error) return null;
  return <Notice tone="danger">{error.message}</Notice>;
}

export function Field({ label, hint, error, children }: { label: string; hint?: ReactNode; error?: string; children: ReactNode }) {
  return (
    <label className="field" data-invalid={error ? "" : undefined}>
      <span className="field-label">{label}</span>
      {children}
      {error ? <span className="field-error">{error}</span> : hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

type BtnProps = {
  children: ReactNode;
  variant?: "primary" | "ghost" | "danger";
  type?: "button" | "submit";
  /** An arrow after the label, for a step forward; on by default for primary. */
  arrow?: boolean;
  /** An icon ahead of the label. */
  icon?: IconName;
  small?: boolean;
  disabled?: boolean;
  onClick?: () => void;
};

/** A button. Primary is ink and turns emerald on hover. */
export function Btn({ children, variant = "ghost", type = "button", arrow, icon, small, disabled, onClick }: BtnProps) {
  return (
    <button type={type} className="btn" data-variant={variant} data-small={small || undefined} disabled={disabled} onClick={onClick}>
      {icon && <Icon name={icon} size={16} />}
      <BtnInner arrow={arrow ?? (variant === "primary" && !icon)}>{children}</BtnInner>
    </button>
  );
}

/** The inside of a button, for links styled as one. */
export function BtnInner({ arrow, children }: { arrow?: boolean; children: ReactNode }) {
  return (
    <>
      <span className="btn-label">{children}</span>
      {arrow && (
        <span className="btn-arrow">
          <Icon name="arrow" size={15} />
        </span>
      )}
    </>
  );
}

/** A modal dialog on the native <dialog>, which traps focus and Esc for us. */
export function Modal({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className="modal" onClose={onClose} aria-labelledby="modal-title">
      {open && (
        <>
          <header className="modal-head">
            <h2 id="modal-title">{title}</h2>
            <button type="button" className="modal-x" aria-label="Close" onClick={onClose}>
              Close
            </button>
          </header>
          <div className="modal-body">{children}</div>
        </>
      )}
    </dialog>
  );
}

/** Asks before a change that is hard to take back. */
export function Confirm({
  label,
  title,
  body,
  confirm,
  variant = "danger",
  disabled,
  small,
  onConfirm,
}: {
  label: string;
  title: string;
  body: ReactNode;
  confirm: string;
  variant?: "primary" | "danger";
  disabled?: boolean;
  small?: boolean;
  onConfirm: () => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Btn variant={variant === "danger" ? "danger" : "ghost"} small={small} disabled={disabled} onClick={() => setOpen(true)}>
        {label}
      </Btn>
      <Modal open={open} title={title} onClose={() => setOpen(false)}>
        <div className="prose">{body}</div>
        <div className="modal-actions">
          <Btn onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn
            variant={variant}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await onConfirm();
              setBusy(false);
              setOpen(false);
            }}
          >
            {busy ? "Working…" : confirm}
          </Btn>
        </div>
      </Modal>
    </>
  );
}

/** A password or secret the server shows once and never again. */
export function ShownOnce({ label, value, children }: { label: string; value: string; children?: ReactNode }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="once">
      <p className="once-label">{label}</p>
      <div className="once-value">
        <code>{value}</code>
        <Btn
          small
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => setCopied(true));
          }}
        >
          {copied ? "Copied" : "Copy"}
        </Btn>
      </div>
      <p className="once-note">{children ?? "This is shown once. Copy it now and send it to the person over a private channel."}</p>
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      {children && <div className="empty-body">{children}</div>}
    </div>
  );
}

export function Loading() {
  return <p className="loading">Loading…</p>;
}

/** A definition list of label and value pairs. */
export function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="facts">
      {items.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** "Nusrat Jahan" → "NJ". */
export function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "")).toUpperCase() || "?";
}

/** A person's initials in a circle. */
export function Avatar({ name, large }: { name: string; large?: boolean }) {
  return (
    <span className="avatar" data-large={large || undefined} aria-hidden="true">
      {initials(name)}
    </span>
  );
}
